import { useQuery } from "@tanstack/react-query";
import { Empty, Table } from "antd";
import { ColumnsType, FilterValue } from "antd/lib/table/interface";
import CsvExport, { useCsvExport } from "components/CsvExport";
import Currency from "components/Currency";
import { useAuthenticatedRequest } from "hooks/useRequests";
import { BreakerTransaction, CustomerBreaker } from "models/breaker";
import { PagedResult, PageRequest } from "models/common";
import { TransactionType, TransactionTypeNames } from "models/transactions";
import { useMemo, useState } from "react";
import { DefaultPageRequest } from "util/getDefaultPageRequest";
import { getPageRequest, localizedDate } from "util/helpers";
import { BreakerTableProps } from "./BreakerTableProps";

const useBreakerTransactionsForCustomer = (breaker: CustomerBreaker | undefined, pageRequest: PageRequest) => {
    const url = `/customer/breakers/${breaker?.id}/transactions`;

    const request = getPageRequest(url, pageRequest);
    const fetchTx = useAuthenticatedRequest<PagedResult<BreakerTransaction>>(request);

    const csvRequest = useMemo(() => {
        return getPageRequest(url, { format: 'csv', filterBy: pageRequest.filterBy });
    }, [url, pageRequest]);
    const csvDownload = useCsvExport(csvRequest, `breaker-transactions-${breaker?.userName}`);

    return {
        ...useQuery(["customer", "breakers", breaker?.id, "transactions", pageRequest], fetchTx, {
            enabled: !!breaker?.id
        }),
        csvDownload
    };
}

const columns: ColumnsType<BreakerTransaction> = [
    {
        title: "Date",
        dataIndex: "transactionDate",
        key: "date",
        render: localizedDate,
    },
    {
        title: "Description",
        dataIndex: "description",
        key: "desc",
    },
    {
        title: "Type",
        dataIndex: "transactionType",
        key: "transactionType",
        render: (t: TransactionType) => TransactionTypeNames[t]
    },
    {
        title: "Amount",
        dataIndex: "grossAmountInCents",
        key: "gross",
        align: "right",
        render: (a) => <Currency cents={a} />,
    },
    {
        title: "Fees",
        dataIndex: "feesInCents",
        key: "fees",
        align: "right",
        render: (a) => <Currency cents={-a} />,
    },
    {
        title: "Net",
        dataIndex: "netAmountInCents",
        key: "net",
        align: "right",
        render: (a) => <Currency cents={a} />,
    },
];

const BreakerTransactionTable = ({ breaker }: BreakerTableProps) => {
    const [{ page, filter, filterBy, withinDays, sort }, setPageRequest] =
        useState<PageRequest>(DefaultPageRequest);

    const {
        data = {
            data: [],
            totalRecords: 0,
            pageSize: 0
        },
        isLoading,
        csvDownload
    } = useBreakerTransactionsForCustomer(breaker, {
        page,
        filter,
        filterBy,
        withinDays,
        sort,
    });

    const { data: records, totalRecords, pageSize } = data;

    const tableChanged = (
        { current: newPage }: any,
        filters: Record<string, FilterValue | null>,
        { column, field, order }: any
    ) => {
        setPageRequest({
            page: newPage,
            filter,
            filterBy: filters,
            withinDays,
            sort: column ? [field, order === "ascend"] : null,
        });
    };

    return <Table
        loading={breaker !== undefined && isLoading}
        columns={columns}
        dataSource={records}
        rowKey={r => r.id}
        onChange={tableChanged}
        showSorterTooltip={false}
        pagination={{
            total: totalRecords,
            current: page,
            pageSize,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ['topRight'],
            showTotal: () => <CsvExport onExport={csvDownload} />,
        }}
        locale={{
            emptyText: <Empty description={breaker ?
                `No transactions found under ${breaker.userName}!` :
                "Click on a breaker to display any transactions you have with that breaker"} />
        }}
    />
}

export default BreakerTransactionTable;